import { useContext, useId, useEffect } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { useInView } from 'react-intersection-observer'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import RadioInput from 'LuxKit/Radio/RadioInput'
import Group from 'components/utils/Group'
import SolidInfoCircleIcon from 'LuxKit/Icons/solid/SolidInfoCircleIcon'
import FormatCurrency from 'components/Common/FormatCurrency'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import styled from 'styled-components'
import RadioGroup from 'LuxKit/Radio/RadioGroup'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import ModalContext from 'contexts/ModalContext'
import CFMRUnforseenEventsModal from 'components/Insurance/UnforseenEventsModal/CFMRUnforseenEventsModal'
import Clickable from 'components/Common/Clickable'
import BedbankCancellationPolicyModal from './BedbankCancellationPolicyModal'
import { trackClientEvent } from 'analytics/analytics'
import useNonAmendableWording from 'components/Common/CancellationPolicies/useNonAmendableWording'

const StyledRadioInput = styled(RadioInput)`
  display: flex;
  align-items: start;
`

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`

interface Props {
  isLoading: boolean
  errorOnQuote: boolean
  quote?: App.InsuranceQuote
  onChange?: (value: 'non-refundable' | 'full-refund') => void
  refundTitle?: string
  refundDetails?: string
  defaultSelected?: 'non-refundable' | 'full-refund'
  enableNonAmendableWording?: boolean
}

function BedbankRoomOptionRateInsurance({ onChange, isLoading, errorOnQuote, quote, refundTitle, refundDetails, defaultSelected = 'non-refundable', enableNonAmendableWording = false }: Props) {
  const intl = useIntl()
  const canShowInsuranceOptions = !isLoading && !errorOnQuote && !!quote?.total
  const {
    nonAmendableDetail,
    nonAmendableLabel,
    showNonAmendableWording,
  } = useNonAmendableWording(enableNonAmendableWording && canShowInsuranceOptions)
  const showModal = useContext(ModalContext)
  const radioGroupName = useId()
  const [impressionRef, impressionInView] = useInView({ triggerOnce: true })

  useEffect(() => {
    if (impressionInView) {
      trackClientEvent({
        subject: 'flex_cfmr_package',
        action: 'impression',
        type: 'operational',
        category: 'insurance',
      })
    }
  }, [impressionInView, isLoading, errorOnQuote, quote])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as 'non-refundable' | 'full-refund'

    if (value === 'full-refund') {
      trackClientEvent({
        subject: 'flex_cfmr_package',
        action: 'select_cfmr_option',
        type: 'interaction',
        category: 'insurance',
      })
    }

    onChange?.(value)
  }

  const handleRefundInfoIconClick = (event: React.MouseEvent) => {
    // Prevent default behavior and stop event bubbling to avoid triggering the radio input selection
    event.preventDefault()
    event.stopPropagation()
    showModal(<CFMRUnforseenEventsModal quote={quote} />)
  }

  const nonRefundableModalHeading = showNonAmendableWording ? nonAmendableLabel : intl.formatMessage({
    id: 'UpDuf8j005ETKz8HZ_Qs',
    defaultMessage: 'Non-refundable and no date changes permitted',
    description: 'Heading for cancellation policy modal when room rate is non-refundable',
  })
  const nonRefundablePolicyDescriptions = showNonAmendableWording ? [nonAmendableDetail] : [intl.formatMessage({
    id: 'rBJ6S1Np5F7874oZmFWH',
    defaultMessage: 'If you cancel your booking, you are not eligible for a refund or credit for a future stay.',
    description: 'Policy description for cancellation policy modal when room rate is non-refundable',
  })]
  const nonRefundableRateLabel = showNonAmendableWording ? nonAmendableLabel : intl.formatMessage({
    id: 'nhSYNzerMAx6FJF857jz',
    defaultMessage: 'Non-refundable',
    description: 'Label indicating a room rate is non-refundable in the insurance section',
  })

  const handleNonRefundableInfoIconClick = () => {
    showModal(
      <BedbankCancellationPolicyModal
        heading={nonRefundableModalHeading}
        policyDescriptions={nonRefundablePolicyDescriptions}
      />,
    )
  }

  if (isLoading) {
    return <LoadingBox height={80} width={230} />
  }

  if (!canShowInsuranceOptions) return null

  return (
    <div ref={impressionRef}>
      <RadioGroup>
        <VerticalSpacer gap={12}>
          <Group direction="horizontal" gap={4}>
            <RadioInput
              name={radioGroupName}
              value="non-refundable"
              onChange={handleChange}
              defaultChecked={defaultSelected === 'non-refundable'}
            >
              {nonRefundableRateLabel}
            </RadioInput>
            <Clickable onClick={handleNonRefundableInfoIconClick}>
              <SolidInfoCircleIcon size="S" />
            </Clickable>
          </Group>
          <StyledRadioInput
            name={radioGroupName}
            value="full-refund"
            onChange={handleChange}
            defaultChecked={defaultSelected === 'full-refund'}
          >
            <Group direction="horizontal" gap={4} verticalAlign="center" wrap="wrap">
              <BodyTextBlock variant="medium" weight="bold" colour="highlight-primary-dark" wrap="no-wrap">
                {refundTitle}
              </BodyTextBlock>
              <BodyTextBlock variant="medium" weight="bold" colour="light-foreground-alt" wrap="no-wrap">
                {intl.formatMessage({
                  id: 's661S9KJhmaS1ys_bfgZ',
                  defaultMessage: '+{price}',
                  description: 'Price with plus sign prefix for insurance add-on cost',
                }, { price: <FormatCurrency value={quote?.total} /> })}
              </BodyTextBlock>
              <BodyTextBlock variant="medium" colour="light-foreground-alt" weight="normal" wrap="no-wrap">
                {refundDetails}
              </BodyTextBlock>
              <Clickable as="span" onClick={handleRefundInfoIconClick}>
                <IconContainer>
                  <SolidInfoCircleIcon colour="light-foreground-alt" />
                </IconContainer>
              </Clickable>
            </Group>
          </StyledRadioInput>
        </VerticalSpacer>
      </RadioGroup>
    </div>
  )
}

export default BedbankRoomOptionRateInsurance
